import { Timestamp, collection, doc, getFirestore, updateDoc, writeBatch } from "firebase/firestore";
import { t } from "i18next";
import * as PopUp from "../../../components/deprecated/Popup/deprecated_PopUps";
import { DataCreator } from "../../../utils/dataCreator";

import { DeviationPriority } from "@LocalData/Deviation/DeviationPriority";
import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";
import { IRoutineFrequency, TRoutineFrequency } from "@propely/propely-typings";
import { deleteField } from "firebase/firestore";
import { Utilities } from "../../../utils/utilities";
import { CompanyData } from "../Company/CompanyData";
import { ContractorGroupData } from "../ContractorGroup/ContractorGroupData";
import { DeviationVisibility, DeviationVisibilityHelper } from "../Deviation/DeviationVisibility";
import { SubtaskFieldData } from "../Deviation/Subtask/SubtaskFieldData";
import { DefaultTaskSettings, TaskSettings } from "../Deviation/TaskSettings";
import CollectionLoader from "../General/CollectionLoader";
import { FirebaseData } from "../General/FirebaseData";
import { FirebaseFile } from "../General/FirebaseFile";
import { LocalData } from "../General/LocalData";
import { PropertyCollectionItemData } from "../Property/Collections/PropertyCollectionItemData";
import { PropertyData } from "../Property/PropertyData";
import { UnitData } from "../Property/Units/UnitData";
import { UserData } from "../User/UserData";
import { RoutineActivityData } from "./RoutineActivityData";
import { RoutineTask } from "./RoutineTask";

export type RoutinePropertyData = {
  data: PropertyData;
  units: UnitData[];
  collections: {
    [key: string]: PropertyCollectionItemData[];
  };
  responsibles: UserData[];
  responsiblesPerUnit: {
    [key: string]: UserData[];
  };
  executors: UserData[];
  executorsPerUnit: {
    [key: string]: UserData[];
  };
};

export type ResponsiblesTypes = "all" | "property" | "unit" | "fireSafetyResponsibles";

export class RoutineData extends FirebaseData {
  companies: CompanyData[] = [];
  properties: RoutinePropertyData[] = [];
  title = "";
  description = "";
  active = false;
  generateUniqueTaskPerUnit: boolean = false;
  startDate?: Date;
  createDaysBefore = 60;
  deadlineDelay?: number;
  frequency?: IRoutineFrequency;
  tags?: string[];
  taskTypeID?: string;
  files: FirebaseFile[] = [];
  floors: number[] = [];
  placeDescription?: string;
  collectionItemIDs: string[] = [];
  settings: TaskSettings = DefaultTaskSettings;
  visibility: DeviationVisibility = DeviationVisibilityHelper.getStandard(undefined);
  endDate?: Date;
  contractorGroups: ContractorGroupData[] = [];
  responsibles: UserData[] = [];
  responsiblesType: ResponsiblesTypes = "all";
  executors: UserData[] = [];
  executorsType: ResponsiblesTypes = "all";
  priority?: DeviationPriority;
  tasks: RoutineTask[] = [];
  taskOrder: string[] = [];
  taskListNumbered = false;
  activities: CollectionLoader<RoutineActivityData>;
  archived = false;

  constructor(id: string, collection: string, parent?: FirebaseData | undefined) {
    super(id, collection, parent);
    this.activities = new CollectionLoader<RoutineActivityData>("activities", this, RoutineActivityData);
  }

  setup(data: any) {
    this.title = typeof data.title === "string" ? data.title : "";
    this.description = typeof data.description === "string" ? data.description : "";
    this.companies = Array.isArray(data.companies) ? data.companies.map((companyID: string) => LocalData.companies.addData(companyID)) : [];
    this.tags = Array.isArray(data.tags) ? data.tags.filter(Boolean) : [];
    this.taskTypeID = typeof data.taskTypeID === "string" ? data.taskTypeID : undefined;
    this.priority = typeof data.priority === "number" ? DeviationPriority.getPriorityWithId(data.priority) : undefined;
    this.placeDescription = typeof data.placeDescription === "string" ? data.placeDescription : "";
    this.active = typeof data.active === "boolean" ? data.active : false;
    this.generateUniqueTaskPerUnit = typeof data.generateUniqueTaskPerUnit === "boolean" ? data.generateUniqueTaskPerUnit : false;
    this.createDaysBefore = typeof data.createDaysBefore === "number" ? data.createDaysBefore : 60;
    this.deadlineDelay = typeof data.deadlineDelay === "number" ? data.deadlineDelay : undefined;
    this.taskListNumbered = typeof data.taskListNumbered === "boolean" ? data.taskListNumbered : false;
    this.collectionItemIDs = Array.isArray(data.collectionItemIDs) ? data.collectionItemIDs : [];

    this.settings =
      typeof data.settings === "object"
        ? {
            checklistRequired: typeof data.settings.checklistRequired === "boolean" ? data.settings.checklistRequired : false,
            checklistFieldsRequired: typeof data.settings.checklistFieldsRequired === "boolean" ? data.settings.checklistFieldsRequired : false,
            attachmentRequired: typeof data.settings.attachmentRequired === "boolean" ? data.settings.attachmentRequired : false,
            commentRequired: typeof data.settings.commentRequired === "boolean" ? data.settings.commentRequired : false,
          }
        : DefaultTaskSettings;

    this.contractorGroups = Array.isArray(data.contractorGroups)
      ? data.contractorGroups.map((contractorID: string) => LocalData.contractorGroups.addData(contractorID))
      : [];
    this.responsibles = Array.isArray(data.responsibles) ? data.responsibles.map((userID: string) => LocalData.users.addData(userID)) : [];
    this.responsiblesType = typeof data.responsiblesType === "string" ? data.responsiblesType : "all";
    this.executors = Array.isArray(data.executors) ? data.executors.map((userID: string) => LocalData.users.addData(userID)) : [];
    this.executorsType = typeof data.executorsType === "string" ? data.executorsType : "all";
    this.floors = Array.isArray(data.floors) ? data.floors : [];
    this.archived = typeof data.archived === "boolean" ? data.archived : false;

    if (this.isValidObject(data.propertyIDs)) {
      this.properties = [];
      for (let propertyId in data.propertyIDs) {
        const propertyData = data.propertyIDs[propertyId];
        let property = LocalData.properties.addData(propertyId);
        const value: RoutinePropertyData = {
          data: property,
          units: [],
          collections: {},
          responsibles: [],
          executors: [],
          responsiblesPerUnit: {},
          executorsPerUnit: {},
        };

        for (const unitID of Array.isArray(propertyData.units) ? propertyData.units : []) {
          const unit = property.getUnitWithId(unitID);
          value.units.push(unit);
        }

        if (propertyData.collections) {
          for (const collectionID of Object.keys(propertyData.collections)) {
            if (Array.isArray(propertyData.collections[collectionID])) {
              const collection = property.collections.addData(collectionID);
              value.collections[collectionID] = [];

              for (const itemID of propertyData.collections[collectionID].filter((item: any) => typeof item === "string")) {
                value.collections[collectionID].push(collection.items.addData(itemID));
              }
            }
          }
        }

        for (const userID of Array.isArray(propertyData.responsibles)
          ? propertyData.responsibles.filter((item: any) => typeof item === "string")
          : []) {
          value.responsibles.push(LocalData.users.addData(userID));
        }
        if (this.isValidObject(propertyData.responsiblesPerUnit)) {
          for (const unitID in propertyData.responsiblesPerUnit) {
            value.responsiblesPerUnit[unitID] = propertyData.responsiblesPerUnit[unitID].map((userID: string) => LocalData.users.addData(userID));
          }
        }

        for (const userID of Array.isArray(propertyData.executors) ? propertyData.executors.filter((item: any) => typeof item === "string") : []) {
          value.executors.push(LocalData.users.addData(userID));
        }
        if (this.isValidObject(propertyData.executorsPerUnit)) {
          for (const unitID in propertyData.executorsPerUnit) {
            value.executorsPerUnit[unitID] = propertyData.executorsPerUnit[unitID].map((userID: string) => LocalData.users.addData(userID));
          }
        }

        this.properties.push(value);
      }
    }

    // StartDate
    if (data.startDate as Timestamp) {
      try {
        this.startDate = (data.startDate as Timestamp).toDate();
      } catch (error) {
        this.startDate = undefined;
      }
    }
    if (this.isValidDate(data.startDate)) {
      this.startDate = data.startDate;
    }

    // Frequency
    const parsedFrequency = TRoutineFrequency.safeParse(data.frequency);
    if (parsedFrequency.success) {
      this.frequency = parsedFrequency.data;
    } else {
      this.frequency = undefined;
    }

    // Tasks
    this.tasks = [];
    if (this.isValidObject(data.tasks)) {
      for (let taskId in data.tasks) {
        const task = this.getTaskWithId(taskId);
        task.setup(data.tasks[taskId]);
      }
    }
    this.taskOrder = Array.isArray(data.taskOrder) ? data.taskOrder : [];

    // Files
    if (this.isValidObject(data.files)) {
      this.files = [];
      for (let fileId in data.files) {
        let fileData = data.files[fileId];
        const path = fileData.path;
        const file = new FirebaseFile(path);
        file.id = fileId;
        file.title = fileData.name;
        file.fileType = fileData.type;
        this.files.push(file);
      }
    }
    // Visibility
    if (this.isValidObject(data.visibility)) {
      this.visibility = {
        landlords: typeof data.visibility.landlords === "boolean" ? data.visibility.landlords : false,
        tenants: typeof data.visibility.tenants === "boolean" ? data.visibility.tenants : false,
        allTenants: typeof data.visibility.allTenants === "boolean" ? data.visibility.allTenants : false,
      };
    }

    super.setup(data);
  }

  canViewRoutine(): boolean {
    return this.companies.some((company) => LocalData.currentUser.companies.includes(company));
  }

  createOrUpdateTask(
    title: string,
    description: string,
    fields: SubtaskFieldData[],
    responsible: UserData[],
    completion: (error?: Error) => void,
    id?: string
  ) {
    const responsibleUsers: { [key: string]: any } = {};
    responsible.forEach((responsibleUser) => {
      responsibleUsers[responsibleUser.id] = {};
    });

    const fieldMap: { [key: string]: any } = {};
    for (const field of fields) {
      fieldMap[field.id] = {
        title: field.title,
        unit: field.unit,
        value: field.value,
        valueType: field.valueType,
      };
    }

    const db = getFirestore();
    let taskId = id ?? doc(collection(db, "routines")).id;

    this.saveUpdateWithCompletionHandler(
      {
        [`tasks.${taskId}`]: {
          title: title,
          description: description,
          responsible: responsibleUsers,
          fields: fieldMap,
        },
      },
      (error) => {
        if (!error) {
          let task = this.getTaskWithId(taskId);
          task.title = title;
          task.description = description;
          task.responsible = responsible;
          if (this.taskOrder.length > 0) {
            const newOrder = [...this.taskOrder, taskId];
            this.updateOrder(newOrder);
          }
        }
        completion(error);
      }
    );
  }

  updateOrder(taskOrder: string[]): Promise<void | string[]> {
    return this.saveUpdate({ taskOrder: taskOrder }).then(() => {
      this.taskOrder = taskOrder;
    });
  }

  getTaskWithId(id: string): RoutineTask {
    for (const task of this.tasks) {
      if (task.id === id) return task;
    }
    const task = new RoutineTask(id);
    this.tasks.push(task);
    return task;
  }

  removeProperties(removeProperties: PropertyData[]): Promise<any> {
    const updateData: { [key: string]: any } = {};
    for (const property of removeProperties) {
      updateData["propertyIDs." + property.id] = deleteField();
    }

    return this.saveUpdate(updateData);
  }

  setContractorGroups(contractors: ContractorGroupData[]) {
    return this.saveUpdate({
      contractorGroups: contractors.map((c) => c.id),
    }).then(() => {
      this.contractorGroups = contractors;
    });
  }

  setResponsibles(usersType: ResponsiblesTypes, responsibles: { [propertyID: string]: UserData[] }): Promise<any> {
    const updateData: { [key: string]: any } = {
      responsiblesType: usersType,
    };

    updateData.responsibles = [];
    if (usersType === "all") {
      updateData.responsibles = responsibles.all?.map((r) => r.id) ?? [];
    } else if (usersType === "property") {
      for (const propertyID in responsibles) {
        updateData["propertyIDs." + propertyID + ".responsibles"] = responsibles[propertyID].map((r) => r.id) ?? [];
      }
    } else if (usersType === "unit") {
      for (const group in responsibles) {
        const [propertyID, unitID] = group.split("-");
        updateData["propertyIDs." + propertyID + ".responsiblesPerUnit." + unitID] = responsibles[group].map((r) => r.id) ?? [];
      }
    }

    return this.saveUpdate(updateData);
  }

  setExecutors(usersType: ResponsiblesTypes, executors: { [id: string]: UserData[] }): Promise<any> {
    const updateData: { [key: string]: any } = {
      executorsType: usersType,
    };

    updateData.executors = [];
    if (usersType === "all") {
      updateData.executors = executors.all?.map((r) => r.id) ?? [];
    } else if (usersType === "property") {
      for (const propertyID in executors) {
        updateData["propertyIDs." + propertyID + ".executors"] = executors[propertyID].map((r) => r.id) ?? [];
      }
    } else if (usersType === "unit") {
      for (const group in executors) {
        const [propertyID, unitID] = group.split("-");
        updateData["propertyIDs." + propertyID + ".executorsPerUnit." + unitID] = executors[group].map((r) => r.id) ?? [];
      }
    }

    return this.saveUpdate(updateData);
  }

  getDuplicationMap(title: string): { [key: string]: any } {
    const tasksMap: { [key: string]: any } = {};
    for (const task of this.tasks) tasksMap[task.id] = task.toMap();

    const data: { [key: string]: any } = {
      title: title,
      description: this.description,
      propertyIDs: {},
      companies: this.companies.map((company) => company.id),
      active: this.active,
      startDate: this.startDate,
      frequency: this.frequency ?? {},
      tags: this.tags ?? [],
      taskTypeID: this.taskTypeID ?? "",
      placeDescription: this.placeDescription ?? "",
      floors: this.floors,
      contractorGroups: this.contractorGroups.map((group) => group.id),
      visibility: this.visibility ?? {},
      tasks: tasksMap,
      createdBy: DataCreator.getCreatedBy(),
    };

    if (this.priority) data.priority = this.priority.id;
    if (this.createDaysBefore) data.createDaysBefore = this.createDaysBefore;
    if (this.deadlineDelay) data.deadlineDelay = this.deadlineDelay;

    return data;
  }

  delete() {
    return this.saveUpdate({
      deletedBy: DataCreator.getDeletedBy(),
    })
      .then(() => {
        LocalData.routines.documents.splice(LocalData.routines.documents.indexOf(this), 1);
      })
      .catch((error) => {
        console.error("There was an error deleting the report: " + error.code + ", message: " + error.message + ", details: " + error.details);
      });
  }

  deleteTask(task: RoutineTask, completion: () => void) {
    ToastUtilities.presentInfo(t("main.deletingTask"));
    const db = getFirestore();
    const routineDocRef = doc(db, "routines", this.id);

    updateDoc(routineDocRef, {
      [`tasks.${task.id}`]: null,
    })
      .then(() => {
        this.tasks = this.tasks.filter((newTask) => newTask !== task);
        if (this.taskOrder.length > 0) {
          const newOrder = this.taskOrder.filter((id) => id !== task.id);
          this.updateOrder(newOrder);
        }
        PopUp.removePopUp();
        completion();
      })
      .catch((error) => {
        console.error("There was an error deleting the routine task: " + error.code + ", message: " + error.message + ", details: " + error.details);
        ToastUtilities.presentError(error.code + ": " + error.message);
      });
  }

  async deleteActivitiesInBulk(startDate: Date | undefined, endDate: Date | undefined, properties: PropertyData[] | undefined): Promise<any> {
    const db = getFirestore();
    const batches = [writeBatch(db)];
    let docCount = 0;

    if (!this.activities.loaded) await this.activities.loadWithPromise();

    this.activities.documents.forEach((activity) => {
      if (
        Utilities.dateWithinRange(activity.date, startDate, endDate) &&
        (!properties || (activity.property && properties.indexOf(activity.property) !== -1))
      ) {
        const activityRef = doc(db, activity.getCompletePath());
        if (docCount >= 499) {
          batches.push(writeBatch(db));
          docCount = 0;
        }
        batches[batches.length - 1].delete(activityRef);
        docCount++;
        if (activity.property?.id && activity.deviation?.id) {
          const deviationRef = doc(db, activity.deviation.getCompletePath());
          if (docCount >= 499) {
            batches.push(writeBatch(db));
            docCount = 0;
          }
          batches[batches.length - 1].set(deviationRef, {
            deletedBy: DataCreator.getDeletedBy(),
          });
          docCount++;
        }
      }
    });
    return Promise.all(
      batches.map((batch) =>
        batch
          .commit()
          .then(() => {})
          .catch((error) => {
            console.log("Error deleting documents");
            console.log(error.debugDescription);
            throw error;
          })
      )
    );
  }

  uploadFile(file: FirebaseFile): Promise<FirebaseFile> {
    const db = getFirestore();

    return new Promise((resolve: (value: FirebaseFile) => void, reject) => {
      file.saveAndLoadURL((success) => {
        if (success) {
          let updateObject = {
            ["files." + file.id]: {
              type: file.fileType,
              name: file.title,
              path: file.path,
            },
            updatedBy: DataCreator.getUpdatedBy(),
          };
          const routineRef = doc(db, "routines", this.id);
          updateDoc(routineRef, updateObject)
            .then(() => {
              this.files.push(file);
              this.activities.documents.forEach((activity) => {
                if (activity.deviation) {
                  if (["jpg", "png", "jpeg"].indexOf(file.fileType ?? "") !== -1) {
                    activity.deviation.images.push(file);
                  } else {
                    activity.deviation.files.push(file);
                  }
                }
              });
              resolve(file);
            })
            .catch((error) => {
              console.log(error);
              reject(error);
            });
        } else {
          reject();
        }
      });
    });
  }

  updateTaskListNumbered(newState: boolean): Promise<any> {
    return this.saveUpdate({ taskListNumbered: newState }).then(() => {
      this.taskListNumbered = newState;
    });
  }

  deleteFile(file: FirebaseFile): Promise<FirebaseFile> {
    const db = getFirestore();

    return new Promise((resolve, reject) => {
      file.deleteFileFromStorage(() => {
        const routineRef = doc(db, "routines", this.id);

        updateDoc(routineRef, {
          ["files." + file.id]: deleteField(),
          updatedBy: DataCreator.getUpdatedBy(),
        })
          .then(() => {
            this.files = this.files.filter((newFile) => newFile !== file);
            this.activities.documents.forEach((activity) => {
              if (activity.deviation) {
                activity.deviation.files = activity.deviation.files.filter((newFile) => newFile.id !== file.id);
                activity.deviation.images = activity.deviation.images.filter((newFile) => newFile.id !== file.id);
              }
            });
            resolve(file);
          })
          .catch((error) => {
            reject(error);
          });
      });
    });
  }

  archive() {
    const newArchiveStatus = !this.archived;
    return this.saveUpdate({ archived: newArchiveStatus, active: !newArchiveStatus }).then(() => {
      this.archived = newArchiveStatus;
      this.active = !newArchiveStatus;
    });
  }

  hasWriteAccess(): boolean {
    return this.companies.some((company) => LocalData.currentUser.companies.includes(company));
  }
}
